import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { FinancialPOAData, financialPOAService } from '../../../../services/financialPOAInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('FinancialPOAData');

type FinancialPOADataScreenRouteProp = RouteProp<RootStackParamList, 'FinancialPOAData'>;

type InfoItemProps = {
  label: string;
  value: string | string[] | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';
  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (Array.isArray(value)) {
    displayValue = value.length > 0 ? value.join(', ') : 'Not specified';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const FinancialPOADataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<FinancialPOADataScreenRouteProp>();
  const [financialPOAInfo, setFinancialPOAInfo] = useState<FinancialPOAData | null>(null);
  const [loading, setLoading] = useState(true);

  const financialPOAId = route.params?.financialPOAId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly;

  const loadFinancialPOAInfo = useCallback(async () => {
    try {
      setLoading(true);
      logger.debug('Loading financial POA info, financialPOAId:', financialPOAId);
      if (financialPOAId) {
        const info = await financialPOAService.getFinancialPOAById(financialPOAId, targetUserId);
        logger.debug('Loaded by ID:', JSON.stringify(info, null, 2));
        setFinancialPOAInfo(info);
      } else {
        const allInfo = await financialPOAService.getAllFinancialPOAs(targetUserId);
        logger.debug('Loaded all:', JSON.stringify(allInfo, null, 2));
        if (allInfo.length > 0) {
          setFinancialPOAInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading financial POA info:', error);
    } finally {
      setLoading(false);
    }
  }, [financialPOAId, targetUserId]);

  useFocusEffect(
    useCallback(() => {
      loadFinancialPOAInfo();
    }, [loadFinancialPOAInfo])
  );

  // Parse powers granted from JSON string
  const getPowersGranted = (): string[] => {
    if (!financialPOAInfo?.powersGranted) return [];
    if (typeof financialPOAInfo.powersGranted === 'string') {
      try {
        return JSON.parse(financialPOAInfo.powersGranted);
      } catch {
        return [];
      }
    }
    return [];
  };

  const handleEdit = () => {
    if (financialPOAInfo) {
      navigation.navigate('FinancialPOAInput', {
        financialPOAData: financialPOAInfo,
        isEditing: true
      });
    } else {
      navigation.navigate('FinancialPOAInput');
    }
  };

  const handleDelete = () => {
    if (financialPOAInfo) {
      Alert.alert(
        'Delete Financial POA Information',
        'Are you sure you want to delete this financial POA information? This action cannot be undone.',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await financialPOAService.deleteFinancialPOA(financialPOAInfo.id!);
                const remainingData = await financialPOAService.getAllFinancialPOAs();
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('financial_poa');
                }
                (navigation as any).navigate('EstatePlanning', { screen: 'EstatePlanningChecklist', params: { refreshKey: Date.now() } });
              } catch (error) {
                logger.error('Error deleting financial POA info:', error);
                Alert.alert('Error', 'Failed to delete financial POA information');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    (navigation as any).navigate('EstatePlanning', { screen: 'EstatePlanningChecklist', params: { refreshKey: Date.now() } });
  };

  const powersGranted = getPowersGranted();

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Image source={require('../../../../assets/icons/back.png')} style={[styles.backIcon, { tintColor: '#fff' }]} />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image source={require('../../../../assets/icons/keyimage.png')} style={styles.backgroundKey} />
        <Image source={require('../../../../assets/logo.png')} style={styles.logo} resizeMode="contain" />
        <Text style={styles.title}>Financial Power of Attorney</Text>
      </View>

      <View style={styles.infoContainer}>
        <ScrollView style={styles.scrollView}>
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#29BFC0" />
              <Text style={styles.loadingText}>Loading...</Text>
            </View>
          ) : !financialPOAInfo ? (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No Financial POA data found</Text>
              <TouchableOpacity
                style={[styles.button, styles.editButton, { marginTop: 20 }]}
                onPress={handleEdit}
              >
                <Text style={styles.buttonText}>Add Financial POA Details</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              <Text style={styles.sectionTitle}>Primary Agent</Text>
              <InfoItem label="Name" value={financialPOAInfo?.agentName} />
              <InfoItem label="Email" value={financialPOAInfo?.agentContact} />
              <InfoItem label="Notified" value={financialPOAInfo?.agentNotified} />

              {(financialPOAInfo?.alternateAgentName || financialPOAInfo?.alternateAgentContact) && (
                <>
                  <Text style={styles.sectionTitle}>Backup Agent</Text>
                  <InfoItem label="Name" value={financialPOAInfo?.alternateAgentName} />
                  <InfoItem label="Email" value={financialPOAInfo?.alternateAgentContact} />
                </>
              )}

              {powersGranted.length > 0 && (
                <>
                  <Text style={styles.sectionTitle}>Powers Granted</Text>
                  <InfoItem label="Powers" value={powersGranted} />
                </>
              )}

              <Text style={styles.sectionTitle}>Document Details</Text>
              <InfoItem label="Effective Date" value={financialPOAInfo?.effectiveDate} />
              <InfoItem label="Document Location" value={financialPOAInfo?.physicalLocation} />

              {financialPOAInfo?.specificInstructions && (
                <InfoItem label="Additional Instructions" value={financialPOAInfo?.specificInstructions} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <TouchableOpacity
                    style={[styles.button, styles.deleteButton]}
                    onPress={handleDelete}
                  >
                    <Text style={styles.buttonText}>Delete</Text>
                  </TouchableOpacity>
                )}

                {!viewOnly && (
                  <TouchableOpacity
                    style={[styles.button, styles.editButton]}
                    onPress={handleEdit}
                  >
                    <Text style={styles.buttonText}>Edit</Text>
                  </TouchableOpacity>
                )}
              </View>
            </>
          )}

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#29BFC0' },
  header: { flexDirection: 'row', alignItems: 'center', padding: 15, paddingTop: 25, position: 'relative' },
  backButton: { position: 'absolute', left: 50, padding: 15, paddingTop: 50, zIndex: 1 },
  backIcon: { width: 32, height: 32, tintColor: '#fff' },
  topSection: { alignItems: 'center', paddingVertical: 20 },
  backgroundKey: { position: 'absolute', width: '85%', height: '85%', opacity: 0.1 },
  logo: { width: 125, height: 125, marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 'bold', color: '#FFFFFF', marginBottom: 8, paddingHorizontal: 30, textAlign: 'center' },
  infoContainer: { flex: 1, backgroundColor: '#F3F6F8', borderTopLeftRadius: 20, borderTopRightRadius: 20, paddingTop: 20 },
  scrollView: { paddingHorizontal: 16, paddingBottom: 120 },
  sectionTitle: { fontSize: 20, fontWeight: 'bold', color: '#1C2C4F', marginBottom: 16, marginTop: 20 },
  infoItem: { marginBottom: 12 },
  label: { fontSize: 14, fontWeight: '600', color: '#6B7280', marginBottom: 4 },
  value: { fontSize: 16, color: '#1C2C4F' },
  separator: { height: 1, backgroundColor: '#E5E5E5', marginTop: 12 },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: { height: 120 },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: ms(50),
  },
  loadingText: {
    marginTop: ms(10),
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
});
